
import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

const WhyWebcollPage = () => {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  useEffect(() => {
    // Update page title and meta description
    document.title = 'Neden Webcoll? | Profesyonel Web Tasarım Farkımız';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Webcoll\'un profesyonel web tasarım hizmetlerinde farkını keşfedin. 3 günde teslim, SEO uyumlu, mobil dostu tasarımlar ve 7/24 teknik destek.');
    }

    // Add structured data
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Neden Webcoll?",
      "description": "Webcoll'un profesyonel web tasarım hizmetlerinde farkını keşfedin",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/neden-webcoll`,
      "mainEntity": {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Webcoll neden tercih edilmeli?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Webcoll, 3 günde teslim garantisi, SEO uyumlu tasarım, mobil uyumluluk, 7/24 teknik destek ve yüksek performanslı altyapı ile öne çıkar."
            }
          },
          {
            "@type": "Question", 
            "name": "Web sitesi ne kadar sürede teslim edilir?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Tüm web sitesi projelerimiz 3 gün içinde teslim edilir ve yayına alınır."
            }
          }
        ]
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const features = [
    {
      icon: 'ri-rocket-line',
      title: '3 Günde Yayında',
      description: 'Hızlı kurulum süreciyle web siteniz kısa sürede yayına alınır. Zaman kaybetmeden dijital dünyada yerinizi alın.'
    },
    {
      icon: 'ri-search-line',
      title: 'SEO Uyumlu ve Google Optimize',
      description: 'Google\'da üst sıralarda yer almanız için SEO optimizasyonu ve teknik altyapı hazır olarak sunulur.'
    },
    {
      icon: 'ri-smartphone-line',
      title: 'Mobil & Tablet Uyumu',
      description: 'Tüm cihazlarda mükemmel görünüm. Responsive tasarım ile her ekran boyutunda profesyonel deneyim.'
    },
    {
      icon: 'ri-palette-line',
      title: 'Tasarım Kişiselleştirme',
      description: 'Markanıza özel renk paleti, logo entegrasyonu ve sektörünüze uygun tasarım özelleştirmeleri.'
    },
    {
      icon: 'ri-customer-service-line',
      title: '7/24 Teknik Destek',
      description: 'Uzman ekibimiz her zaman yanınızda. Teknik sorunlarınız için kesintisiz destek hizmeti sunuyoruz.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Yüksek Performans & Güvenli Altyapı',
      description: 'Hızlı yükleme süreleri, SSL sertifikası ve güvenli hosting altyapısı ile güvenilir web deneyimi.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-white"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-32 h-32 bg-blue-500 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-purple-500 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Her pikselinde{' '}
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              profesyonellik
            </span>{' '}
            var.
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Webcoll, hız, estetik ve SEO'yu bir araya getirir. İşletmenizin dijital dünyada fark yaratması için gereken her şey burada.
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`relative p-8 bg-white rounded-2xl border border-gray-100 shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer group ${
                  hoveredCard === index ? 'transform -translate-y-2' : ''
                }`}
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl"></div>
                
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${feature.icon} text-2xl text-white`}></i>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors duration-300">
                    {feature.title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-purple-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-blue-400 to-purple-500 transform rotate-12 scale-150"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Sen de farkı keşfet
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Webcoll'un profesyonel çözümlerini keşfedin ve markanızı dijital dünyada öne çıkarın.
          </p>
          
          <a
            href="/cozumlerimiz"
            className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-full hover:bg-gray-50 transition-all duration-300 transform hover:scale-105 shadow-xl whitespace-nowrap"
          >
            Çözümlerimizi Gör
            <i className="ri-arrow-right-line ml-2 text-lg"></i>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WhyWebcollPage;
